import MultiDecimalHelper from "@/utils/multi-decimal"
import subCancelReasonCodeConfig from "@/utils/sub-cancel-reason-code-config"
import { PRICING_MODEL } from "@/utils/pricing-model-constants"

export default class PortalHelper {
  static constructOrderItemFromSubData(subData) {
    let output = JSON.parse(JSON.stringify(subData.orderItem));
    return output;
  }

  static cloneAddons(addons) {
    return JSON.parse(JSON.stringify(addons));
  }

  // Keeps portal change-subscription cart display in sync with the estimate API response.
  // Called from change-order calculate_estimate after injectAddonUnitPrices runs and
  // update_subscription returns; the synced orderItem is passed to replace_order_item.
  // Matches subscription_estimate.line_items to cart plan/addons by entity_id and copies
  // amount / unit_amount fields so preview totals reflect injected addon pricing (CHKOUTENGG-54375).
  static syncCartAmountsFromSubscriptionEstimate(orderItem, subscriptionEstimate) {
    if (!orderItem || !subscriptionEstimate?.line_items?.length) {
      return orderItem;
    }

    const lineItems = subscriptionEstimate.line_items;
    const planTypes = new Set(['plan', 'plan_item_price']);
    const addonTypes = new Set(['addon', 'addon_item_price']);

    const syncAmounts = (cartEntity, lineItem) => {
      if (!cartEntity || !lineItem) return;
      cartEntity.amount = lineItem.amount;
      if (lineItem.amount_in_decimal != null) {
        cartEntity.amount_in_decimal = lineItem.amount_in_decimal;
      }
      if (lineItem.unit_amount != null) {
        cartEntity.unit_amount = lineItem.unit_amount;
      }
      if (lineItem.unit_amount_in_decimal != null) {
        cartEntity.unit_amount_in_decimal = lineItem.unit_amount_in_decimal;
      }
    };

    const planLineItem = lineItems.find(
      li => planTypes.has(li.entity_type) && li.entity_id === orderItem.plan?.id
    );
    syncAmounts(orderItem.plan, planLineItem);

    (orderItem.addons || []).forEach(addon => {
      const addonLineItem = lineItems.find(
        li => li.entity_id === addon.id && addonTypes.has(li.entity_type)
      );
      syncAmounts(addon, addonLineItem);
    });

    return orderItem;
  }

  static getDetailsFromCart(cart) {
    return {
      planDetail: cart.orderItems[0].plan.details,
      addonDetails: cart.orderItems[0].addons.map(({details}) => details)
    }
  }

  static displayPeriod(days, trial_unit) {
    return `${days} day${days > 1 ? 's' : ''}`;
  }

  static enableChangeSub(changedOrderItem, oldSubData) {
    var oldOrderItem = oldSubData.orderItem
    var oldItems = [].concat(oldOrderItem.plan, oldOrderItem.addons);

    // Compare addon IDs from existing sub (if any ) with the new addon IDs added to the cart
    
    const oldAddons = JSON.stringify(oldOrderItem.addons.map(addons => addons.id).sort());
    const newAddons = JSON.stringify(changedOrderItem.addons.map(addons => addons.id).sort());

      if(oldAddons !== newAddons){
        return true;
      }

    return oldItems.filter(item => {
      if(item.type == "plan") {
        return (item.id != changedOrderItem.plan.id) 
        || ((item.quantity_in_decimal != changedOrderItem.plan.quantity_in_decimal)
        || ((item.quantity || 1) != (changedOrderItem.plan.quantity || 1)));
      }
      if(item.type == "addon") {
        let t = changedOrderItem.addons.filter(i => i.id == item.id)[0];
        return (t && (t.quantity_in_decimal != item.quantity_in_decimal)) || (t && (t.quantity != item.quantity));
      }
    }).length > 0 || !!PortalHelper.hasCouponChanges(changedOrderItem.coupon_list , oldSubData.recurringEstimate && oldSubData.recurringEstimate.discounts);
  }

  static hasCouponChanges(newCouponList, oldDiscount) {
    return (newCouponList && oldDiscount) ? newCouponList.length != oldDiscount.length
              || newCouponList.filter(coupon => !oldDiscount.some(discount => discount.entity_id == coupon.id)).length > 0
              || oldDiscount.filter(discount => !newCouponList.some(coupon => coupon.id == discount.entity_id)).length > 0
          : (newCouponList && newCouponList.length > 0 && !oldDiscount) || (!newCouponList && oldDiscount && oldDiscount.length > 0);
  }

  static filterAddonsBasedOnPlanTerm(addons, plan, includeNonRecAddons) {
    var selected_addons = []
    switch(plan.period_unit) {
      case "week":
        selected_addons = addons.filter(a => a.period_unit == "week" && (plan.period % a.period) == 0);
        selected_addons = selected_addons.concat(addons.filter(a => a.period_unit == "day" && ((plan.period * 7) % a.period) == 0))
        break;
      case "month":
        selected_addons = addons.filter(a => a.period_unit == "month" && (plan.period % a.period) == 0);
        break;
      case "year":
        selected_addons = addons.filter(a => a.period_unit == "year" && (plan.period % a.period) == 0);
        selected_addons = selected_addons.concat(addons.filter(a => a.period_unit == "month" && ((plan.period * 12) % a.period) == 0))
        break;
      case "day":
        selected_addons = addons.filter(a => a.period_unit == "day" && (plan.period % a.period) == 0);
        break;
    }

    // Non-recurring addons
    if(includeNonRecAddons) { 
      selected_addons = selected_addons.concat(addons.filter(a => {
        return (!a.period && (!a.period_unit || a.period_unit == "not_applicable")) || a.item_type == 'charge'
      }))
    }
    return selected_addons;
  }

  static constructOrderItemForOtherProducts(other_product, subData, getters) {
    var current_addons = this.cloneAddons(subData.orderItem.addons);
    
    // Check if the new plan supports quantity by looking up its pricing model
    const itemPriceList = (subData.other_product_items && subData.other_product_items.item_prices) || [];
    const newPlanItemPrice = itemPriceList.find(
      itemPrice => itemPrice.id === other_product.plan_id
    );
    const newPlanSupportsQuantity = newPlanItemPrice && newPlanItemPrice.pricing_model !== PRICING_MODEL.FLAT_FEE;
    
    // Persist current quantity if the new plan supports quantity
    const currentCartQuantity = getters.change_order_plan?.quantity;
    const originalQuantity = subData.orderItem.plan.quantity;
    const quantity = newPlanSupportsQuantity ? (currentCartQuantity || originalQuantity || 1) : 1;
    
    var orderItem = {
      plan: {
        id: other_product.plan_id,
        quantity: quantity,
        type: "plan"
      }
    }
    if(subData.isMultiDecimal && newPlanSupportsQuantity) {
      // First try current cart state, then fall back to original subscription
      const currentCartQuantityInDecimal = getters.change_order_plan?.quantity_in_decimal;
      const originalQuantityInDecimal = subData.orderItem.plan.quantity_in_decimal;
      orderItem.plan.quantity_in_decimal = currentCartQuantityInDecimal || originalQuantityInDecimal || MultiDecimalHelper.getDefaultQuantity();
    }
    orderItem.plan.initialQuantity = MultiDecimalHelper.getInitialQuantity(orderItem.plan);

    if(other_product.plan_id == subData.orderItem.plan.id)  {
      orderItem.addons = [].concat(current_addons);
    } else {
      let addonsList = current_addons;
      if(other_product.is_addons_restricted) {
        addonsList = addonsList.filter(addon => !!other_product.allowed_addons.find(_addon => _addon.id === addon.id))
      }
      var selected_addons = [];
      switch(subData.retain_addons_for_change && other_product.period_unit) {
        case "week":
          var selected_addons = addonsList.filter(a => a.details.period_unit == "week" && (other_product.period % a.details.period) == 0);
          break;
        case "month":
          var selected_addons = addonsList.filter(a => a.details.period_unit == "month" && (other_product.period % a.details.period) == 0);
          break;
        case "year":
          var selected_addons = addonsList.filter(a => a.details.period_unit == "year" && (other_product.period % a.details.period) == 0);
          selected_addons = selected_addons.concat(addonsList.filter(a => a.details.period_unit == "month" && ((other_product.period * 12) % a.details.period) == 0))
          break;
        case "day":
          var selected_addons =  addonsList.filter(a => a.details.period_unit == "day" && (other_product.period % a.details.period) == 0);
          break;
      }

      // union of other_product.addons and selected_addons
      let o = [].concat(selected_addons);
      let tmp = selected_addons.map(({id}) => id);
      other_product.addons.forEach(a => {
        if(a.id && tmp.indexOf(a.id) == -1) {
          o.push(a);
        }
      });
      orderItem.addons = o;
    }

    const currentOrderItemAddons = (getters.order_items || {}).addons || [];
    orderItem.addons.forEach(addon => {
      // TODO - Remove this hack added as part of CHKOUTENGG-13846
      const currentAddon = currentOrderItemAddons.find(_addon => _addon.id === addon.id);
      if(currentAddon) {
        addon.added_type = currentAddon.added_type;
      }
      // --- CHKOUTENGG-13846

      addon.type = "addon";
      addon.quantity = addon.quantity || 1;
      if(subData.isMultiDecimal) {
        addon.quantity_in_decimal = MultiDecimalHelper.getQuantity(addon) || MultiDecimalHelper.getDefaultQuantity();
      }
      addon.initialQuantity = MultiDecimalHelper.getInitialQuantity(addon);
    });
    // orderItem.coupon = hostedPage.coupon;
    // orderItem.flattened = [].concat(orderItem.plan, orderItem.addons);
    return orderItem;
  }

  static canShowBreakup(estimate) {
    return estimate && (this.hasPricingModelItem(estimate)
                || (estimate.taxes && estimate.taxes.length > 0)
                || (estimate.discounts && estimate.discounts.length > 0));
  }

  static hasPricingModelItem(estimate) {
    if(estimate.line_items && estimate.line_items.length > 0){
      return estimate.line_items.some(i => {
        switch(i.entity_type) {
          case "plan":
          case "addon":
          return i.pricing_model == "tiered";
        }
      });
    }
  }

  static getCancelReasonText(ref, subData) {
    let reasonValue = subCancelReasonCodeConfig.getLocalizedTextByCode(subData.cancel_reason_code)
    if(subData.isScheduledForCancellation()) {
      return ref.t("subscription.sch_cancel.reason_code.info", {
        reason: reasonValue, sub_cancel_reason_code: reasonValue 
      });
    }
    return ref.t("subscription.canceled.reason_code.info", {
      reason: reasonValue, sub_cancel_reason_code: reasonValue 
    });
  }

  static isAddonRemoved(oldSubData, changeOrderData) {
    const oldAddonIds = new Set((oldSubData.addons || []).map(addon => addon.id));
    const newAddonIds = new Set((changeOrderData.addons || []).map(addon => addon.id));
    for (const id of oldAddonIds) {
      if (!newAddonIds.has(id)) {
        return true;
      }
    }
    return false;
  }

  static buildCouponChangeList(oldList, newList) {
      const all = Array.from(new Set([...oldList, ...newList]));
      return all.reduce((acc, code) => {
        const inOld = oldList.includes(code);
        const inNew = newList.includes(code);
        if (inOld && inNew) acc.push(`${code} - No change`);
        else if (!inOld && inNew) acc.push(`${code} - Added`);
        else if (inOld && !inNew) acc.push(`${code} - Removed`);
        return acc;
      }, []);
  }

  static buildDetailedChangeList(oldList, newList) {
      const all = Array.from(new Set([...oldList, ...newList]));
      return all.map(id => {
        if (oldList.includes(id) && newList.includes(id)) return `${id} - No change`;
        if (!oldList.includes(id) && newList.includes(id)) return `${id} - Added`;
        if (oldList.includes(id) && !newList.includes(id)) return `${id} - Removed`;
      });
  }

  static getItemsByType(items, type) {
    return (items || []).filter(item => item.item_type === type).map(item => item.item_price_id)
  }

  static getAddonRemovalMailContent(oldSub, newSub, oldSubCoupons) {
    const today = new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});

    const oldPlans = this.getItemsByType(oldSub.subscription_items, 'plan');
    const oldAddons = this.getItemsByType(oldSub.subscription_items, 'addon');
    const oldCharges = this.getItemsByType(oldSub.subscription_items, 'charge');
    const oldCoupons = (oldSubCoupons || []).map(c => c.code);

    const newCoupons = (newSub.coupon_list || []).map(c => c.code);
    const newPlan = newSub.plan?.id;
    const newAddons = (newSub.addons || []).map(a => a.id);
    const newCharges = (newSub.event_based_addons || []).map(c => c.id);

    const addonChanges = this.buildDetailedChangeList(oldAddons, newAddons);
    const chargeChanges = this.buildDetailedChangeList(oldCharges, newCharges);
    const couponChanges = this.buildCouponChangeList(oldCoupons, newCoupons);

    let changeContent = {}

    if(oldPlans[0] !== newPlan) {
      changeContent.plan = [
        `Current Plan: ${oldPlans[0] || ''}`,
        `New Plan: ${newPlan || ''}`
      ]
    }
    else {
      changeContent.plan = [
        `${oldPlans[0]} - no change`
      ]
    }

    if (addonChanges.length > 0) {
      changeContent.addons = []
      addonChanges.forEach(change => changeContent.addons.push(change));
    }

    if (chargeChanges.length > 0) {
      changeContent.charges = []
      chargeChanges.forEach(change => changeContent.charges.push(change));
    }

    if (couponChanges.length > 0) {
      changeContent.coupons = []
      couponChanges.forEach(change => changeContent.coupons.push(change));
    }

    changeContent.change_date = today

    return JSON.stringify(changeContent)
  }
}